import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  SECTION_ANCHORS,
  SECTION_LABELS,
  type LookbookSection,
} from "./lookbook-visibility";

/**
 * Jump links for a deal page that has more than one section.
 *
 * Anchors rather than tabs: the page is one long read (pitch, files, reports,
 * the thread), and most deals show one or two sections, so tabs would be
 * chrome over nothing to switch between. The link for the section in view is
 * underlined in ink as you scroll.
 */
export function LookbookSectionNav({
  sections,
}: {
  sections: LookbookSection[];
}) {
  const links = [
    ...sections.map((section) => ({
      id: SECTION_ANCHORS[section],
      label: SECTION_LABELS[section],
    })),
    { id: "lb-comments", label: "Comments" },
  ];
  const [active, setActive] = useState<string>(links[0]?.id ?? "");
  const ids = links.map((l) => l.id).join(",");

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const targets = ids
      .split(",")
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (targets.length === 0) return;
    // The band just under the two sticky bars decides which section is "in
    // view", so the underline moves as a heading reaches the top.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -55% 0px" }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, [ids]);

  if (sections.length < 2) return null;

  // Pinned under the ink nav (h-14) within the content column, so it never
  // runs underneath the engagement rail beside it.
  return (
    <nav
      aria-label="Sections"
      className="sticky top-14 z-20 -mt-2 mb-8 border-b border-border bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-background/80"
    >
      <div className="flex gap-6 overflow-x-auto [scrollbar-width:none] sm:gap-8">
        {links.map((link) => (
          <a
            key={link.id}
            href={`#${link.id}`}
            aria-current={active === link.id ? "true" : undefined}
            className={cn(
              "relative shrink-0 py-3.5 text-[13.5px] font-medium transition-colors duration-150",
              active === link.id
                ? "text-foreground after:absolute after:inset-x-0 after:-bottom-px after:h-[2px] after:bg-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {link.label}
          </a>
        ))}
      </div>
    </nav>
  );
}

/**
 * One part of a deal page on paper: a large tight heading, an optional
 * two-digit count beside it, a quiet note on the right, and the content under
 * an ink rule, the same rule the lookbook's list sits under.
 */
export function DealSection({
  id,
  title,
  count,
  meta,
  labelledBy,
  className,
  children,
}: {
  id?: string;
  title: ReactNode;
  count?: number;
  meta?: ReactNode;
  /** Id for the heading, when something else points at it. */
  labelledBy?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={cn("scroll-mt-32 pt-12 sm:pt-14", className)}
    >
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2 pb-5">
        <h2
          id={labelledBy}
          className="text-[clamp(1.35rem,1.2rem+0.6vw,1.75rem)] font-semibold leading-none tracking-[-0.03em]"
        >
          {title}
          {count !== undefined && count > 0 && (
            <span className="ml-3 align-top text-[0.45em] font-medium tabular-nums tracking-normal text-muted-foreground">
              {String(count).padStart(2, "0")}
            </span>
          )}
        </h2>
        {meta && <div className="text-[13px] text-muted-foreground">{meta}</div>}
      </div>
      <div className="border-t border-foreground">{children}</div>
    </section>
  );
}
